/* eslint-disable react-hooks/exhaustive-deps */
import { useLazyQuery, useQuery } from '@apollo/client';
import cx from 'classnames';
import pick from 'lodash/pick';
import {
  CSSProperties, FormEvent, useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { parseISO, isToday } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import Button from '../components/Button';
import OrderSummary from '../components/OrderSummary';
import { PasswordInput } from '../components/PasswordInput';
import PreviousSelector, { Selection } from '../components/PreviousSelector';
import TextInput from '../components/TextInput';
import {
  selectAddOns, AddOn,
} from '../features/addOns/addOnsSlice';
import { login } from '../features/auth/authSlice';
import {
  selectPotentialFranchiseId,
  selectPaymentPreference,
  selectPaymentPreferenceTouched,
  selectPrepayDiscountPercent,
  selectSlotPrice,
  selectPlanVisitFeeCents,
  applyDefaultPaymentPreference,
  setPaymentPreference,
  withdrawPrepay,
  setSlotPrice,
  selectReferralCode,
  selectPayerSelected,
  selectPayerEmail,
  selectPayerName,
  setPayerSelected,
  setPayerDetails,
} from '../features/booking/bookingSlice';
import { isValidPayerEmail, sameEmail } from '../util/payerPayment';
import {
  bookingPhoneOrUndefined,
  checkBookingPhone,
  formatPhoneInput,
} from '../util/phone';
import { goBackTo } from '../util/stepNavigation';
import { slotInstant } from '../util/slotTime';
import { readReferral } from '../util/referralLink';
import {
  eligibleSubtotalCents as calcEligibleSubtotalCents,
  isPlanBooking,
  orderSubtotalCents,
  PaymentPreference,
  prepayDiscountCents,
  slotAdjustmentCents,
  withPlanVisitFee,
} from '../util/bookingPricing';
import PaymentPreferenceSelector, {
  PAYER_CHOICE,
  PaymentChoice,
} from '../components/PaymentPreferenceSelector';
import { setPostalCode } from '../features/postalCode/postalCodeSlice';
import {
  Appointment,
  Contact,
  setContact, setDateTime,
  setEmail,
  setName,
  setNewContact,
  setPhone,
  setServiceCoverage,
} from '../features/serviceLocation/serviceLocationSlice';
import useAuthentication from '../hooks/useAuthentication';
import styles from '../sass/components/Schedule.module.scss';
import {
  ROUTE_BOOK_ADD_ONS,
  ROUTE_BOOK_SERVICES,
} from '../util/constants';
import {
  mp,
  getDistinctId,
  getDeviceId,
} from '../util/analytics';
import { aprTrack } from '../util/aperioTracking';
import { getBookingLang } from '../util/bookingLang';
import {
  BookingDraftPayload,
  useBookingDraftCapture,
} from '../hooks/useBookingDraftCapture';
import { getFieldErrors } from '../util/getFieldErrors';
import { focusFirstInvalid as focusFirstInvalidIn } from '../util/formA11y';
import { selectServiceTypes } from '../features/serviceTypes/serviceTypesSlice';
import useBookingSubmit, { NETWORK_ERROR } from '../hooks/useBookingSubmit';
import useSlotFees from '../hooks/useSlotFees';
import Alert from '../components/Alert';
import { authenticate } from './Login';
import {
  GET_CONTACTS_BY_USER_ID,
  GET_POSTAL,
  GET_VENDOR_LIST,
  GET_USER_BY_ID,
  GET_FRANCHISE_VANS,
  GET_TECHNICIAN_VANS,
} from '../util/gql';
import AppointmentDatePicker from '../components/AppointmentDatePicker';
import Select from '../components/EditorSelect';
import { Vendor } from '../interfaces/Customer';
import { Van } from '../interfaces/Van';
import { discountedFeesNames } from '../constants/discountedFees';
import { isServiceNameKey } from '../util/serviceKeys';
import { CUSTOMER_FACING_ERRORS } from '../util/validationErrorHelper';
import ServiceAddressSummary from '../components/ServiceAddressSummary';
import { useTenant } from '../components/TenantContext';
import { normalizeThemePreset } from '../util/themePreset';
import { formatMoney, formatWholeDollars } from '../i18n/format';
import { portalLanguage } from '../i18n';
// Imported last so the CSS bundle's module order is what it was before.
import BookingSummaryBar from '../components/BookingSummaryBar';

/** Read by assistive tech, never drawn (the classic clip pattern). */
/** The privacy policy's texting section, in the reader's language. */
function textingPolicyHref(): string {
  const lang = portalLanguage();
  return `https://aperiomobility.com${lang === 'fr' || lang === 'es' ? `/${lang}` : ''}/privacy#texts`;
}

const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export default function Schedule() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // Class hooks for a tenant theme preset's stylesheet (see
  // public/aperio/theme.css, "booking steps"). False for every tenant on the
  // default preset, so their class lists are unchanged.
  const themePreset = normalizeThemePreset(useTenant()?.themePreset);
  const themed = themePreset !== 'default';
  // Aperio booking-step copy and pricing display (plan offer redesign). False
  // for every other tenant, whose step renders exactly as before.
  const aperio = themePreset === 'aperio';
  // Aperio: the step starts at its top. The previous step is usually left
  // scrolled far down (the plan offer's button), and the router keeps that
  // offset, which landed customers on the site footer.
  useEffect(() => {
    if (aperio) window.scrollTo(0, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { loggedIn: isLoggedIn } = useAuthentication();
  const [password, setPassword] = useState('');
  // Aperio: express email-marketing consent, never pre-ticked, optional.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  // The booking submit, shared with the consumer skin (hooks/
  // useBookingSubmit): idempotency, repricing and the refusals it reports.
  const {
    submit: submitBooking,
    submitting,
    loading: bookingLoading,
    constraints,
    setConstraints,
    errorMessage,
    setErrorMessage,
    errorCode,
    setErrorCode,
    priceChangedMessage: slotPriceChangedMessage,
    availabilityRefreshToken,
  } = useBookingSubmit();
  const [isGuest, setIsGuest] = useState(true);
  // QA 2026-09-25 #30. A format error shows once the field is left (or the
  // booking is attempted), not from the first keystroke; an attempt with
  // something missing marks every missing field and takes focus there.
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const touch = (field: string) => setTouched(
    (prev) => (prev[field] ? prev : { ...prev, [field]: true }),
  );
  const [attempted, setAttempted] = useState(false);
  const [focusRequest, setFocusRequest] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const missingRef = useRef<HTMLSpanElement>(null);
  const [isVendor, setIsVendor] = useState(false);
  const [vendor, setVendor] = useState<Vendor | undefined>();
  const [newVendor, setNewVendor] = useState<boolean>(false);
  const [
    isValidAddress,
    setIsValidAddress,
  ] = useState<undefined | boolean>(undefined);
  const {
    selectedAddress,
    selectedContact,
    serviceAddress,
    inService,
    dateTime,
    contact: {
      email, name, phoneNumber,
    },
  } = useAppSelector((s) => s.serviceLocation);
  const paymentPreference = useAppSelector(selectPaymentPreference);
  const paymentPreferenceTouched = useAppSelector(
    selectPaymentPreferenceTouched,
  );
  const prepayDiscountPercent = useAppSelector(selectPrepayDiscountPercent);
  // "Send the bill to your partner" (Aperio): chosen, and whom to email.
  const payerSelected = useAppSelector(selectPayerSelected);
  const payerEmail = useAppSelector(selectPayerEmail);
  const payerName = useAppSelector(selectPayerName);
  const slotPrice = useAppSelector(selectSlotPrice);

  const selectedServices = useAppSelector(selectServiceTypes);
  const selectedAddOns = useAppSelector(selectAddOns);
  const potentialFranchiseId = useAppSelector(selectPotentialFranchiseId);
  // Prepay is only offered where someone serves the address: a franchise
  // was found for it and the service-area check did not say no. The server
  // enforces the same (PrepaymentService.prepayRefusal); this keeps the
  // customer from choosing what would be refused.
  const prepayAvailable = !!potentialFranchiseId && inService !== false;
  const car = useAppSelector((s) => s.car);

  // Abandoned-booking capture (tenant-gated: Tenant Settings -> Capture
  // drafts). Posted when the email field blurs with a valid address, and
  // again when the slot changes after that; see hooks/useBookingDraftCapture.
  const captureDraftsEnabled = useTenant()?.captureDrafts === true;
  const buildBookingDraft = (): BookingDraftPayload | null => {
    if (selectedContact !== 'new' && selectedContact !== 'unset') return null;
    const slot = dateTime?.date ? slotInstant(dateTime) : null;
    return {
      email: email || '',
      name: name || undefined,
      // Only a number we could book with; never a half-typed one (QA #22).
      phone: bookingPhoneOrUndefined(phoneNumber),
      address: serviceAddress
        ? pick(serviceAddress, [
          'street', 'city', 'province', 'provinceCode', 'postalCode',
          'country', 'countryCode', 'lat', 'lng',
        ])
        : undefined,
      vehicle: (car?.year || car?.make || car?.model || car?.vin)
        ? {
          year: car.year || undefined,
          make: car.make || undefined,
          model: car.model || undefined,
          vin: car.vin || undefined,
        }
        : undefined,
      services: selectedServices.map((s) => ({
        id: s.id, name: s.name, nameKey: s.nameKey || undefined,
      })),
      addOns: selectedAddOns.map((a) => ({
        id: a.id, name: a.name, nameKey: a.nameKey || undefined,
      })),
      bookingType: isPlanBooking(selectedServices) ? 'plan' : 'one-time',
      scheduledAt: slot && !Number.isNaN(slot.getTime())
        ? slot.toISOString() : undefined,
      lang: getBookingLang(),
      deviceId: getDeviceId(),
    };
  };
  const {
    capture: captureBookingDraft,
    hasCaptured: hasCapturedBookingDraft,
  } = useBookingDraftCapture(captureDraftsEnabled, buildBookingDraft);
  useEffect(() => {
    if (hasCapturedBookingDraft()) captureBookingDraft();
  }, [dateTime]);

  // Services subtotal the slot price percentage applies to (fees excluded),
  // and the full pre-tax subtotal the prepay discount applies to.
  const eligibleSubtotalCents = useMemo(
    () => calcEligibleSubtotalCents([...selectedServices, ...selectedAddOns]),
    [selectedServices, selectedAddOns],
  );
  const orderCents = useMemo(
    () => orderSubtotalCents([...selectedServices, ...selectedAddOns]),
    [selectedServices, selectedAddOns],
  );
  const prepaySavingsCents = Math.abs(prepayDiscountCents(
    orderCents + (slotPrice?.cents ?? 0),
    prepayDiscountPercent,
  ));

  // Aperio: what the time slots quote. The mobile service fee joins the
  // basket only once a time is picked, so until then a plan basket is short
  // of it; count it from the start so the slots, the plan card and the
  // bottom bar all quote the same "due at your first visit". Once the fee is
  // in the basket these equal the plain figures above.
  // Selected as a number: a config object would be a new object on every
  // call, which would re-render this step on every store update.
  // The All-Inclusive plan's own fee on an All-Inclusive basket.
  const nextVisitFeeCents = useAppSelector(selectPlanVisitFeeCents);
  const slotQuoteItems = useMemo(
    () => withPlanVisitFee(
      [...selectedServices, ...selectedAddOns],
      nextVisitFeeCents,
    ),
    [selectedServices, selectedAddOns, nextVisitFeeCents],
  );
  const slotEligibleCents = aperio
    ? calcEligibleSubtotalCents(slotQuoteItems)
    : eligibleSubtotalCents;
  const slotDisplayCents = aperio
    ? orderSubtotalCents(slotQuoteItems)
    : orderCents;
  const planBooking = isPlanBooking(selectedServices);
  // Aperio plan bookings where every time costs the same: no "lowest price"
  // banner, legend or per-slot price (set by the date picker once the
  // availability is in). Every other booking shows them as before.
  const hideUniformSlotPrices = aperio && planBooking;
  const [slotPricesVary, setSlotPricesVary] = useState(false);
  const showLowestPriceLegend = eligibleSubtotalCents > 0
    && (!hideUniformSlotPrices || slotPricesVary);

  // Keep the stored slot adjustment in sync with the chosen time and the
  // current basket (e.g. an add-on added after the time was picked).
  useEffect(() => {
    if (!dateTime) {
      if (slotPrice) dispatch(setSlotPrice(null));
      return;
    }
    const cents = slotAdjustmentCents(dateTime, eligibleSubtotalCents);
    if (
      !slotPrice
      || slotPrice.cents !== cents
      || slotPrice.percent !== (dateTime.priceAdjustmentPercent ?? 0)
    ) {
      dispatch(setSlotPrice({
        cents,
        percent: dateTime.priceAdjustmentPercent ?? 0,
        tier: dateTime.priceTier ?? null,
        isLowestPrice: !!dateTime.isLowestPrice,
      }));
    }
  }, [dateTime, eligibleSubtotalCents, slotPrice, dispatch]);

  // Aperio's default is "Pay after service" (owner, 2026-10), first and
  // preselected, as on every other tenant; "Prepay & save" sits beside it.
  // The slice already starts on pay-later. A booking still in sessionStorage
  // from when Aperio defaulted to prepay carries that default (untouched,
  // prepay): put it back on pay-later. A choice the customer made is never
  // overridden (the reducer is a no-op once touched). A default-preset tenant
  // never gets here.
  useEffect(() => {
    if (
      aperio
      && !paymentPreferenceTouched
      && paymentPreference === PaymentPreference.prepay
    ) {
      dispatch(applyDefaultPaymentPreference(PaymentPreference.payLater));
    }
  }, [aperio, paymentPreferenceTouched, paymentPreference, dispatch]);

  // No franchise for the address, or it is not served: pay at the visit.
  useEffect(() => {
    if (!prepayAvailable && paymentPreference === PaymentPreference.prepay) {
      dispatch(withdrawPrepay());
    }
  }, [prepayAvailable, paymentPreference, dispatch]);

  const handlePaymentPreferenceChange = useCallback(
    (value: PaymentChoice) => {
      // Sending the bill is a prepay booking whose link goes to the payer.
      dispatch(setPayerSelected(value === PAYER_CHOICE));
      dispatch(setPaymentPreference(
        value === PAYER_CHOICE ? PaymentPreference.prepay : value,
      ));
      mp.track('Booking - Payment Preference Selected', {
        // Only the choice: never the payer's email or name.
        paymentPreference: value === PAYER_CHOICE ? 'partner' : value,
        prepayDiscountPercent,
        prepaySavingsCents,
        isLoggedIn: !!isLoggedIn,
        anonId: getDistinctId(),
        path: window.location.pathname,
      });
    },
    [dispatch, prepayDiscountPercent, prepaySavingsCents, isLoggedIn],
  );

  const currentUser = useAppSelector((
    state,
  ) => state.auth.currentUser);
  const carId = useAppSelector((state) => state.car.id);
  const customerId = useAppSelector((state) => state.customer.id);
  const [vans, setVans] = useState<Van[]>([]);
  const [selectedVanId, setSelectedVanId] = useState<number | null>(null);
  const [discountAmount, setDiscountAmount] = useState<number | null>(null);
  const [discountType, setDiscountType] = useState<string | null>(null);
  const [discountCodeId, setDiscountCodeId] = useState<string | undefined>('');
  const [discountFeesAmount, setDiscountFeesAmount] = useState(0);

  // Stable identity: Invoice re-runs its discount effects when this changes.
  const handleDiscountUpdate = useCallback((amount: number | null,
    type: string | null,
    feesAmount: number,
    codeId?: string) => {
    setDiscountAmount(amount);
    setDiscountType(type);
    setDiscountFeesAmount(feesAmount);
    setDiscountCodeId(codeId);
  }, []);

  const [loadUserInformation,
    { data: userInfo }] = useLazyQuery(GET_USER_BY_ID);

  const isManager = currentUser.roles?.includes('manager');
  const isTechnician = currentUser.roles?.includes('technician');
  const isTester = currentUser.roles?.includes('tester');
  const isSales = currentUser.roles?.includes('sales');
  const isStaff = !!(isManager || isTechnician || isTester || isSales);

  // "Send the bill to your partner": the Aperio preset only, and only where
  // prepay is on offer (a franchise serves a covered address; the server
  // refuses the rest). Never on a fleet booking or one staff make.
  const payerAvailable = aperio && prepayAvailable && !isStaff && !isVendor;
  const payerActive = payerAvailable
    && payerSelected
    && paymentPreference === PaymentPreference.prepay;
  const paymentChoice: PaymentChoice = payerActive
    ? PAYER_CHOICE
    : paymentPreference;
  useEffect(() => {
    if (payerSelected && !payerAvailable) dispatch(setPayerSelected(false));
  }, [payerSelected, payerAvailable, dispatch]);

  // A friend's share link (`?ref=CODE`, see hooks/useReferralLink): sent with
  // the booking for the server to check and apply, guest or not. Aperio
  // preset only, never on a booking staff make for a customer, and not when
  // a code was picked in the discount box (that one wins).
  const storedReferralCode = useAppSelector(selectReferralCode);
  const [linkReferralCode] = useState(() => readReferral());
  const referralCode = aperio && !isStaff
    ? (storedReferralCode || linkReferralCode)
    : null;
  const friendRewardCents = Number(useTenant()?.referralFriendRewardCents) || 0;

  // Customers book as guests by default and can add a password afterwards.
  // Staff creating a customer keep the password-first flow.
  useEffect(() => {
    if (isStaff) setIsGuest(false);
  }, [isStaff]);

  const [getVans] = useLazyQuery(GET_FRANCHISE_VANS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const franchiseVans = data?.getFranchiseVans || [];
      setVans(franchiseVans);
      if (franchiseVans.length === 1) {
        setSelectedVanId(franchiseVans[0].id);
      }
    },
  });

  const [getTechnicianVan] = useLazyQuery(GET_TECHNICIAN_VANS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const technicianVans = data?.getTechnicianVans || [];
      setSelectedVanId(technicianVans[0]?.id);
    },
  });

  useEffect(() => {
    if ((isManager || isTester || isSales) && potentialFranchiseId) {
      getVans({ variables: { franchiseId: potentialFranchiseId } });
    } else if (isTechnician && potentialFranchiseId) {
      getTechnicianVan({ variables: { technicianId: currentUser.id } });
    }
  }, [isManager,
    isTechnician,
    isTester,
    isSales,
    potentialFranchiseId,
    getVans,
    getTechnicianVan,
    currentUser.id]);

  useEffect(() => {
    if (currentUser && Object.keys(currentUser).length > 1) {
      const userId = customerId || currentUser.id;
      loadUserInformation({ variables: { userId } });
    }
  }, [customerId, currentUser]);

  const {
    data: contactsData,
    loading: contactsLoading,
    error: contactsError,
  } = useQuery(GET_CONTACTS_BY_USER_ID, {
    variables: { userId: customerId || currentUser.id },
    skip: !isLoggedIn,
    fetchPolicy: 'network-only',
  });

  let contacts: Contact[] = useMemo(() => [], []);

  if (contactsData
    && (((isManager
    || isTechnician
    || isSales
    || isTester) && customerId)
    || (!isManager
    && !isTechnician
    && !isTester
    && !isSales))) {
    contacts = contactsData.getContactsByUserId
      .map((c: Contact) => pick(c, ['name', 'email', 'phoneNumber']));
    if (selectedContact === 'unset' && contacts.length > 0) {
      dispatch(setContact([0, contacts[0]]));
    }
  }

  const contactStrings = contacts
    .map((c) => `${c.name}: ${c.phoneNumber} | ${c.email}`);

  useQuery(GET_POSTAL, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const { isInService, code } = data.getPostal;
      if (isInService) {
        dispatch(setPostalCode(code));
      }
      // A saved address is checked here too (enforceCoverage tenants): one
      // we do not serve sends the customer back to the address step.
      dispatch(setServiceCoverage(!!isInService));
      setIsValidAddress(isInService);
    },
    variables: {
      code: serviceAddress?.postalCode,
      lat: serviceAddress?.lat ?? null,
      lng: serviceAddress?.lng ?? null,
      country: serviceAddress?.countryCode || null,
      city: serviceAddress?.city || null,
      region: serviceAddress?.provinceCode || null,
      deviceId: getDeviceId() || null,
      lang: getBookingLang(),
    },
    skip: !serviceAddress?.postalCode,
  });

  useEffect(() => {
    if (serviceAddress?.postalCode) {
      dispatch(setPostalCode(serviceAddress?.postalCode));
    }
  }, [serviceAddress?.postalCode, dispatch]);

  const [
    getVendorList, {
      data: vendorList,
    },
  ] = useLazyQuery(GET_VENDOR_LIST);

  useEffect(() => {
    if (isVendor) {
      getVendorList();
      // Fleet bookings are invoiced to the fleet; prepay does not apply.
      dispatch(setPaymentPreference(PaymentPreference.payLater));
    }
  }, [getVendorList, isVendor, dispatch]);

  // The fee list loaded below, for a fee line the server adds to the basket.
  const feeCatalogRef = useRef<AddOn[]>([]);

  const handleNextClick = async () => {
    const discountAmountUpdated = discountAmount ? Math.round(
      (discountAmount + discountFeesAmount) * 100,
    ) : null;
    await submitBooking({
      discountCents: discountAmountUpdated,
      discountCodeId: discountCodeId || null,
      ...((!isLoggedIn
        || isManager
        || isTechnician
        || isTester
        || isSales)
      && !customerId
      && !isGuest && { password }),
      ...(isVendor && { vendor }),
      vanId: selectedVanId,
      payerActive,
      payerEmail,
      payerName,
      isStaff,
      ...(aperio && !isStaff && marketingOptIn
        && { marketingEmailOptIn: true }),
      referralCode,
      feeCatalog: feeCatalogRef.current,
      onBooked: () => {
        if (!isLoggedIn && !isGuest) {
          const onLoginSuccess = (
            token: string,
            _roles: string[],
            _name: string,
            id: number,
          ) => {
            dispatch(login({ email, token, id }));
          };
          authenticate(
            { email, password },
            onLoginSuccess,
            (message: string) => setErrorMessage(message),
            (message: any) => setConstraints(message),
          );
        }
      },
    });
  };
  const vendorId = userInfo?.getUserById?.vendorId || null;

  // The fee lines a picked time adds (hooks/useSlotFees, shared with the
  // consumer skin's day step).
  const { feesData: data, feeCatalog, applySlotFees } = useSlotFees(vendorId);
  feeCatalogRef.current = feeCatalog;

  const hasDiscountedPrice = [
    ...selectedServices,
    ...selectedAddOns,
  ].some((item) => item.discountedValue !== null
  && item.discountedValue !== undefined);

  const handleSetDateTime = useCallback((date: Appointment) => {
    dispatch(setDateTime(date));
    aprTrack('appointment_selected');
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const iso = date?.date || null;
    const timeStr = (date as any)?.time ?? (date as any)?.startTime ?? null;

    mp.track('Booking - DateTime Selected', {
      dateIso: iso,
      time: timeStr,
      isToday: iso ? isToday(parseISO(iso)) : false,
      timezone: tz,
      isLoggedIn: !!isLoggedIn,
      anonId: getDistinctId(),
      path: window.location.pathname,
      slotPriceTier: date?.priceTier ?? null,
      slotPriceAdjustmentPercent: date?.priceAdjustmentPercent ?? 0,
      slotIsLowestPrice: !!date?.isLowestPrice,
    });

    applySlotFees(date);

    if (data) {
      // Group discount logic
      if (discountAmount && discountAmount > 0 && !discountType) {
        const discountedFees = selectedAddOns.filter(
          (addOn) => isServiceNameKey(addOn.nameKey)
    && discountedFeesNames.includes(addOn.nameKey),
        );
        const discountOnFees = discountedFees.reduce(
          (total, fee) => total + fee.value, 0,
        );
        setDiscountFeesAmount(discountOnFees);
      }
    }
  }, [dispatch, data, selectedAddOns, selectedServices, userInfo,
    applySlotFees]);

  useEffect(() => {
    if (data && dateTime) {
      handleSetDateTime(dateTime);
    }
  }, [data, dateTime, handleSetDateTime]);

  // A new day picked on a phone, its time not yet: the booking has no time.
  const clearDateTime = useCallback(() => {
    if (dateTime) dispatch(setDateTime(undefined as unknown as Appointment));
  }, [dateTime, dispatch]);

  const nameTimer = useRef<number | null>(null);
  const emailTimer = useRef<number | null>(null);
  const phoneTimer = useRef<number | null>(null);

  useEffect(() => {
    if (selectedContact !== 'new') return;
    if (nameTimer.current) window.clearTimeout(nameTimer.current);
    nameTimer.current = window.setTimeout(() => {
      mp.track('Booking - Contact Field Entered', {
        field: 'name',
        valueLength: (name || '').length,
        isLoggedIn: !!isLoggedIn,
        anonId: getDistinctId(),
        path: window.location.pathname,
      });
    }, 500);
  }, [name, selectedContact, isLoggedIn]);

  useEffect(() => {
    if (selectedContact !== 'new') return;
    if (emailTimer.current) window.clearTimeout(emailTimer.current);
    emailTimer.current = window.setTimeout(() => {
      // Never the address or its domain: only whether it looks complete.
      mp.track('Booking - Contact Field Entered', {
        field: 'email',
        looksValid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim()),
        valueLength: (email || '').length,
        isLoggedIn: !!isLoggedIn,
        anonId: getDistinctId(),
        path: window.location.pathname,
      });
    }, 500);
  }, [email, selectedContact, isLoggedIn]);

  useEffect(() => {
    if (selectedContact !== 'new') return;
    if (phoneTimer.current) window.clearTimeout(phoneTimer.current);
    phoneTimer.current = window.setTimeout(() => {
      const digits = (phoneNumber || '').replace(/\D/g, '');
      mp.track('Booking - Contact Field Entered', {
        field: 'phone',
        digitsCount: digits.length,
        valueLength: (phoneNumber || '').length,
        isLoggedIn: !!isLoggedIn,
        anonId: getDistinctId(),
        path: window.location.pathname,
      });
    }, 500);
  }, [phoneNumber, selectedContact, isLoggedIn]);
  // The contact fields being typed: a new contact, or nothing picked yet
  // with no saved contact to pick (every guest until their first keystroke).
  // A guest who pressed "Lock in my booking" before typing used to get no
  // field errors and no scroll to the fields, only the line in the bar.
  const enteringContact = selectedContact === 'new'
    || (selectedContact === 'unset' && !contactsLoading && !contacts.length);
  const validateEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    .test(v.trim());
  const phoneCheck = checkBookingPhone(phoneNumber);

  const isEmailFormatValid = enteringContact
    ? validateEmail(email || '') : true;

  const isPhoneFormatValid = enteringContact
    ? phoneCheck.valid : true;

  const showEmailError = attempted || !!touched.email;
  const showPhoneError = attempted || !!touched.phone;
  let emailFormatError: string | undefined;
  if (enteringContact && showEmailError) {
    if (!email) {
      emailFormatError = attempted ? t('errors.emailRequired') : undefined;
    } else if (!isEmailFormatValid) {
      emailFormatError = t('errors.email');
    }
  }

  let phoneFormatError: string | undefined;
  if (enteringContact && showPhoneError) {
    if (!phoneNumber) {
      phoneFormatError = attempted ? t('errors.phoneRequired') : undefined;
    } else if (!isPhoneFormatValid) {
      phoneFormatError = phoneCheck.error || t('errors.phone');
    }
  }

  // The payer's email: required while that option is chosen, a real
  // address, and not the booker's own.
  const payerEmailValid = isValidPayerEmail(payerEmail);
  const payerIsBooker = sameEmail(payerEmail, email);
  const payerValid = !payerActive || (payerEmailValid && !payerIsBooker);
  let payerEmailError: string | undefined;
  if (payerActive && (attempted || touched.payerEmail)) {
    if (!payerEmail.trim()) {
      payerEmailError = attempted ? t('errors.payer.emailRequired') : undefined;
    } else if (!payerEmailValid) {
      payerEmailError = t('errors.email');
    } else if (payerIsBooker) {
      payerEmailError = t('errors.payer.self');
    }
  }

  const nameMissingError = enteringContact && attempted && !name
    ? t('errors.nameRequired')
    : undefined;

  // Drafts are posted from the email field (tenant setting): say so where
  // the details are typed (QA 2026-09-25 #22).
  const customerEmailHint = [
    t('schedule.emailHint'),
    captureDraftsEnabled ? t('schedule.draftHint') : '',
  ].filter(Boolean).join(' ');

  const newContactValid = !!name
  && !!email
  && !!phoneNumber
  && isEmailFormatValid
  && isPhoneFormatValid;

  const contactValid = (selectedContact !== 'new'
  && selectedContact !== 'unset')
    || newContactValid;
  // A saved address counts as valid unless the service-area check said no.
  const addressValid = isValidAddress !== false
    && ((selectedAddress !== 'new'
    && selectedAddress !== 'unset')
    || isValidAddress);
  const isPasswordFieldVisible = (
    (!isLoggedIn
      || isManager
      || isTechnician
      || isTester
      || isSales) && !customerId
  );

  const isPasswordRequired = isPasswordFieldVisible && !isGuest;
  const passwordTooShort = isPasswordRequired
  && password.length >= 0
  && password.length < 6;
  const hasPasswordOrIsGuestOrIsLoggedIn = isLoggedIn
  || isGuest
  || (isPasswordRequired && password.length >= 6);

  // Never an empty or $0 order (QA #9: after a reload of the vehicle step
  // the page sent vehicleId null and no line items). Staff may book a $0
  // visit on purpose; they still need a vehicle and a service.
  const orderReady = !!carId
    && selectedServices.length > 0
    && (isStaff || orderCents > 0);

  const shouldEnableNext = contactValid
    && addressValid
    && dateTime
    && orderReady
    && payerValid
    && hasPasswordOrIsGuestOrIsLoggedIn;

  // A refusal the customer can fix here (a phone number, the time, the
  // basket) keeps the button, and goes away once they change something.
  const recoverableError = CUSTOMER_FACING_ERRORS.includes(errorCode)
    || errorCode === NETWORK_ERROR;

  // What is still missing, each named (QA 2026-09-25 #30: "fill in your
  // contact details" did not say which field).
  const missingItems: string[] = [];
  if (!orderReady) missingItems.push(t('schedule.missing.order'));
  if (!dateTime) missingItems.push(t('schedule.missing.time'));
  if (!contactValid) {
    if (!enteringContact) {
      missingItems.push(t('schedule.missing.contact'));
    } else {
      if (!name) missingItems.push(t('schedule.missing.name'));
      if (!phoneNumber || !isPhoneFormatValid) {
        missingItems.push(t('schedule.missing.phone'));
      }
      if (!email || !isEmailFormatValid) {
        missingItems.push(t('schedule.missing.email'));
      }
    }
  }
  if (!payerValid) missingItems.push(t('schedule.missing.payerEmail'));
  if (!addressValid) missingItems.push(t('schedule.missing.address'));
  if (!hasPasswordOrIsGuestOrIsLoggedIn) {
    missingItems.push(t('schedule.missing.password'));
  }
  const missingText = missingItems.length
    ? t('schedule.missing.prefix', { list: missingItems.join(', ') })
    : '';
  const timeMissingError = attempted && !dateTime
    ? t('schedule.missing.timeError')
    : undefined;

  // "Lock in my booking" with something missing: every missing field says
  // so and focus goes to the first of them (the date, then the contact
  // fields, in page order), or to the "To continue" line when the gap is
  // not a field on this page. The button stays focusable and clickable
  // (aria-disabled) for exactly this; it used to be a silent grey button.
  const handleLockIn = () => {
    if (shouldEnableNext) {
      handleNextClick();
      return;
    }
    setAttempted(true);
    setFocusRequest((n) => n + 1);
  };
  useEffect(() => {
    if (!focusRequest) return;
    if (!focusFirstInvalidIn(formRef.current)) missingRef.current?.focus();
  }, [focusRequest]);
  useEffect(() => {
    if (!recoverableError || errorCode === NETWORK_ERROR) return;
    setErrorMessage('');
    setErrorCode('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber, dateTime, selectedServices, carId]);

  const onChangeContactSelector = useCallback((s: Selection) => {
    if (s === 'new' || s === 'unset') {
      dispatch(setNewContact());
    } else {
      dispatch(setContact([s, contacts[s]]));
    }

    if (s !== 'new' && s !== 'unset') {
      // Which saved contact, never its name, email or phone.
      mp.track('Booking - Contact Selected', {
        mode: 'existing',
        index: typeof s === 'number' ? s : null,
        isLoggedIn: !!isLoggedIn,
        anonId: getDistinctId(),
        path: window.location.pathname,
      });
    }
  }, [dispatch, contacts]);

  const handleChangeVendor = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const vendorSelect = vendorList.getVendors.find(
      (v: Vendor) => v.name === event.target.value,
    );
    setVendor(() => ({
      vendorId: vendorSelect.id,
      name: vendorSelect.name,
      remittance: vendorSelect.remittance,
      isRemittanceOnSubTotal: vendorSelect.isRemittanceOnSubTotal,
    }));
  };

  // eslint-disable-next-line max-len
  const handleRemittanceChange = (event: FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    setVendor((prevVendor) => ({
      ...prevVendor,
      remittance: Number(target.value),
    } as Vendor));
  };

  // eslint-disable-next-line max-len
  const handleNewVendorChange = (event: FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    setVendor((prevVendor) => ({
      ...prevVendor,
      name: target.value,
    } as Vendor));
  };

  // eslint-disable-next-line max-len
  const handleIsRemittanceOnSubTotalChange = (event: FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    setVendor((prevVendor) => ({
      ...prevVendor,
      isRemittanceOnSubTotal: target.checked,
    } as Vendor));
  };

  // The contact fields. Aperio puts them right after the time (on a phone
  // they sat ~1,600px down, under the payment cards); every other tenant
  // keeps them after the payment choice.
  const contactSection = (
    <div className={styles.formHeader}>
      <h4 aria-level={2}>{t('schedule.contact.title')}</h4>
      <p>{t('schedule.contact.intro')}</p>
      <PreviousSelector
        data={contactStrings}
        newLabel={contactStrings.length
          ? t('schedule.contact.addAnother')
          : t('schedule.contact.yours')}
        loading={contactsLoading}
        error={!!contactsError}
        name="contact"
        selected={selectedContact}
        onChange={onChangeContactSelector}
        childClassName={styles.formSubsection}
      >

        <TextInput
          id="name"
          name="name"
          value={name}
          title={t('schedule.contact.name')}
          autoComplete="name"
          required
          errorMessage={nameMissingError}
          className={styles.input}
          onChange={
            (event: FormEvent<HTMLInputElement>) => (
              dispatch(setName(event.currentTarget.value)))
          }
        />

        <TextInput
          id="phone"
          title={t('schedule.contact.phone')}
          className={styles.input}
          errorMessage={
            phoneFormatError
            ?? getFieldErrors(constraints, ['contact', 'phoneNumber'])
          }
          value={phoneNumber}
          // +1 is the country code, never the area code; an
          // international number or an extension is kept as typed
          // and flagged (util/phone, QA #11).
          onChange={
            (event: FormEvent<HTMLInputElement>) => (
              dispatch(setPhone(
                formatPhoneInput(event.currentTarget.value),
              )))
          }
          onBlur={() => touch('phone')}
          placeholder="(___) ___-____"
          type="tel"
          name="phone"
          autoComplete="tel"
          required
        />
        {/* What the booking texts this number (Twilio A2P 10DLC opt-in
            disclosure, 2026-10-28): the same words as the privacy policy's
            "Texts and calls from us". */}
        {aperio && !isStaff && (
          <p className={styles.textNote}>
            {t('schedule.textNote')}
            {' '}
            <a href={textingPolicyHref()} target="_blank" rel="noopener noreferrer">
              {t('schedule.textNoteLink')}
            </a>
          </p>
        )}
        <div className={styles.passwordBox}>
          <TextInput
            id="email"
            value={email}
            title={t('schedule.contact.email')}
            type="email"
            autoComplete="email"
            className={styles.input}
            errorMessage={
              emailFormatError
              ?? getFieldErrors(constraints,
                ['contact', 'email'])
            }
            onChange={
            (event: FormEvent<HTMLInputElement>) => (
              dispatch(setEmail(event.currentTarget.value)))
          }
            onBlur={() => {
              touch('email');
              captureBookingDraft();
            }}
            name="email"
            required
            hint={aperio && !isStaff
              ? customerEmailHint
              : 'The customer will receive a confirmation at '
                + 'this email address.'}
          />
          {(isManager
          || isTechnician
          || isTester
          || isSales)
          && !customerId && (
            <label className={styles.bookAsVendor} htmlFor="vendor">
              <input
                id="vendor"
                type="checkbox"
                checked={isVendor}
                onChange={() => setIsVendor(!isVendor)}
              />
              <span>Book appointment for vendor</span>
            </label>
          )}
          {aperio && !isStaff && (
            <label className={styles.bookAsGuest} htmlFor="marketing-email-opt-in">
              <input
                id="marketing-email-opt-in"
                type="checkbox"
                checked={marketingOptIn}
                onChange={(e) => setMarketingOptIn(e.currentTarget.checked)}
              />
              <span>{t('schedule.marketingOptIn')}</span>
            </label>
          )}
        </div>

        { isPasswordFieldVisible && !isStaff && (
          <div className={styles.passwordBox}>
            <label
              className={styles.bookAsGuest}
              htmlFor="create-account"
            >
              <input
                id="create-account"
                type="checkbox"
                checked={!isGuest}
                onChange={() => setIsGuest(!isGuest)}
              />
              <span>{t('schedule.account.create')}</span>
            </label>
            {!isGuest && (
              <PasswordInput
                value={password}
                autocomplete="new-password"
                placeholder={t('schedule.account.choosePassword')}
                className={styles.input}
                onChange={
                  (event) => setPassword(event.currentTarget.value)
                }
                title={t('schedule.account.password')}
                id="booking-password"
                name="new-password"
                required
                onBlur={() => touch('password')}
                errorMessage={
                  passwordTooShort
                    && (attempted || touched.password)
                    ? t('errors.passwordShort')
                    : getFieldErrors(constraints, ['password'])
                }
                hint={t('schedule.account.passwordHint')}
              />
            )}
          </div>
        )}
        { isPasswordFieldVisible && isStaff && (
          <div className={styles.passwordBox}>
            <PasswordInput
              value={password}
              autocomplete="current-password"
              placeholder="Password"
              className={styles.input}
              onChange={
                (event) => setPassword(event.currentTarget.value)
              }
              title="Password"
              errorMessage={
                passwordTooShort
                  ? 'Password must be at least 6 characters.'
                  : getFieldErrors(constraints, ['password'])
              }
              hint={'Log in with this password '
                + 'anytime to manage your bookings.'}
              disabled={isGuest}
            />
            <label className={styles.bookAsGuest} htmlFor="guest">
              <input
                id="guest"
                type="checkbox"
                checked={isGuest}
                onChange={() => setIsGuest(!isGuest)}
              />
              <span>Book appointment as guest</span>
            </label>
          </div>
        )}
        {isVendor && (
        <>
          { vendorList && (
          <div className={styles.containerNewVendor}>
            <br />
            <p>
              Select a fleet or add a new one if the customer`s
              fleet is not listed in the dropdown menu.
            </p>
          </div>
          )}
          { vendorList && !newVendor && (
          <div className={styles.container}>
            <Select
              label="Select fleet from list"
              loading={false}
              id="vendor-input"
              placeholder="Select Fleet"
              value={vendor?.name?.toString() || ''}
              onChange={handleChangeVendor}
            >
              {vendorList.getVendors.map((vendorSelect: Vendor) => (
                <option
                  key={vendorSelect.vendorId}
                  value={vendorSelect?.vendorId?.toString()}
                >
                  {vendorSelect?.name}
                </option>
              ))}
            </Select>
            <TextInput
              className={styles.individualItem}
              title="remittance fee %"
              placeholder="fee"
              type="number"
              value={vendor?.remittance
                ? vendor.remittance.toString() : ''}
              min={0}
            />
            <label
              className={styles.bookAsGuest}
              htmlFor="isRemittenceOnSubTotal"
            >
              <input
                id="isRemittenceOnSubTotal"
                type="checkbox"
                checked={!!vendor?.isRemittanceOnSubTotal}
                onChange={handleIsRemittanceOnSubTotalChange}
                disabled
              />
              <span>Remittance is calculated based on sub total</span>
            </label>
          </div>
          )}
          { vendorList && (
          <div className={styles.containerNewVendor}>
            <br />
            <Button
              onClick={() => {
                setVendor(({
                  vendorId: 0,
                  name: '',
                  remittance: 0,
                  isRemittanceOnSubTotal: true,
                }));
                setNewVendor((prevState) => !prevState);
              }}
              variant="primary"
              size="small"
              className={styles.addButton}
            >
              {newVendor ? 'Select vendor from list' : 'New Vendor'}
            </Button>
          </div>
          )}

          {newVendor && (
          <div className={styles.container}>
            <TextInput
              className={styles.individualItemName}
              title="Fleet Name"
              placeholder="Enter fleet name"
              type="text"
              value={vendor?.name}
              onChange={handleNewVendorChange}
            />

            <TextInput
              className={styles.individualItem}
              title="Remittance Fee %"
              placeholder="fee"
              type="number"
              value={vendor?.remittance?.toString()}
              min={0}
              name="remittance"
              onChange={handleRemittanceChange}
            />
            <label
              className={styles.bookAsGuest}
              htmlFor="isRemittenceOnSubTotal"
            >
              <input
                id="isRemittenceOnSubTotal"
                type="checkbox"
                checked={vendor?.isRemittanceOnSubTotal || false}
                onChange={handleIsRemittanceOnSubTotalChange}
              />
              <span>Remittance calculates based on Sub Total</span>
            </label>
          </div>
          )}
        </>
        )}
      </PreviousSelector>

    </div>
  );

  return (
    <div className={cx(styles.schedule, { 'apr-step': themed })}>
      <div className={styles.header}>
        <h1 className={styles.headerDesktop}>{t('schedule.title')}</h1>
        {/* The phone-sized copy of the h1; the step's sections are level 2
            (h4 only for their look), so the outline has no gaps (QA #29). */}
        <h4 className={styles.headerMobile} aria-level={1}>
          {t('schedule.title')}
        </h4>
      </div>
      <div
        className={cx(styles.body, 'container--fluid', {
          'apr-step__body': themed,
        })}
      >
        <div className={cx(styles.bodyLeft, 'container__col-xs-8')}>
          <div className={styles.serviceLocationForm}>
            <form ref={formRef} noValidate>
              <div className={styles.formHeaderContainer}>
                <div className={styles.formHeader}>
                  <h4 aria-level={2}>{t('schedule.heading')}</h4>
                  <p>{t('schedule.provideTime')}</p>
                </div>

                <div className={styles.bookingLegend}>
                  <h4 aria-level={3}>{t('schedule.legend.title')}</h4>
                  <div className={styles.legendItem}>
                    <span className={`${styles.colorBox}`} />
                    { isManager || isSales
                      ? 'No bookings scheduled'
                      : t('schedule.legend.available') }
                  </div>
                  <div className={styles.legendItem}>
                    <span className={`${styles.colorBox} ${styles.blue}`} />
                    { isManager || isSales
                      ? 'A booking already exists for this slot'
                      : t('schedule.legend.request') }
                  </div>
                  {showLowestPriceLegend && (
                    <div className={styles.legendItem}>
                      <span className={`${styles.colorBox} ${styles.green}`} />
                      {t('schedule.tier.lowest')}
                    </div>
                  )}
                </div>
              </div>

              <div className={cx(styles.formSection, styles.bookingContainer)}>
                <div className={styles.datePickerContainer}>
                  <AppointmentDatePicker
                    onSelect={handleSetDateTime}
                    value={dateTime}
                    timeNoLongerAvailable={
                      errorCode === 'TIME_SLOT_UNAVAILABLE'
                    }
                    showPrices={eligibleSubtotalCents > 0}
                    eligibleSubtotalCents={slotEligibleCents}
                    displaySubtotalCents={slotDisplayCents}
                    refreshToken={availabilityRefreshToken}
                    autoSelectBestDeal={!isStaff}
                    planBooking={planBooking}
                    hideUniformPrices={hideUniformSlotPrices}
                    onPricesVaryChange={setSlotPricesVary}
                    onClear={clearDateTime}
                    presetCopy={aperio}
                    errorMessage={timeMissingError}
                  />
                </div>
                {dateTime?.isBooked && !isStaff && (
                  <p className={styles.requestNotice}>
                    <span className="label">{t('schedule.request.title')}</span>
                    {' '}
                    {t('schedule.request.body')}
                  </p>
                )}
                {slotPriceChangedMessage && (
                  <div
                    className={cx(styles.lineItem, styles.priceChangedNotice)}
                    role="alert"
                  >
                    <span className={styles.strongText}>
                      {t('schedule.priceUpdated')}
                    </span>
                    {' '}
                    {slotPriceChangedMessage}
                  </div>
                )}
              </div>

              {aperio && contactSection}

              <div className={styles.formHeader}>
                <h4 aria-level={2}>{t('schedule.pay.title')}</h4>
                {aperio ? (
                  <p>
                    {(() => {
                      if (payerActive) return t('schedule.pay.freePayer');
                      return paymentPreference === PaymentPreference.prepay
                        && !isVendor
                        ? t('schedule.pay.freePrepay')
                        : t('schedule.pay.free');
                    })()}
                    {' '}
                    {t('schedule.pay.choose')}
                  </p>
                ) : (
                  <p>
                    Booking is free and no payment is taken today.
                    Choose what works for you.
                  </p>
                )}
                <PaymentPreferenceSelector
                  value={paymentChoice}
                  onChange={handlePaymentPreferenceChange}
                  prepayDiscountPercent={prepayDiscountPercent}
                  prepaySavingsCents={prepaySavingsCents}
                  disabled={isVendor}
                  prepayAvailable={prepayAvailable}
                  payerAvailable={payerAvailable}
                />
                {payerActive && (
                  // Revealed under the options while "Send the bill to your
                  // partner" is chosen. Styled by `apr-payer-fields` in
                  // public/aperio/theme.css (Aperio only).
                  <fieldset className="apr-payer-fields">
                    <legend className="apr-payer-fields__legend">
                      {t('payment.payer.legend')}
                    </legend>
                    <TextInput
                      id="payer-email"
                      name="payerEmail"
                      type="email"
                      title={t('payment.payer.email')}
                      value={payerEmail}
                      // Never the booker's own address from autofill.
                      autoComplete="off"
                      required
                      className={styles.input}
                      errorMessage={payerEmailError}
                      hint={t('payment.payer.hint')}
                      onChange={(event: FormEvent<HTMLInputElement>) => (
                        dispatch(setPayerDetails({
                          email: event.currentTarget.value,
                        })))}
                      onBlur={() => touch('payerEmail')}
                    />
                    <TextInput
                      id="payer-name"
                      name="payerName"
                      title={t('payment.payer.name')}
                      value={payerName}
                      autoComplete="off"
                      maxlength={40}
                      className={styles.input}
                      onChange={(event: FormEvent<HTMLInputElement>) => (
                        dispatch(setPayerDetails({
                          name: event.currentTarget.value,
                        })))}
                    />
                  </fieldset>
                )}
                {!prepayAvailable && !isVendor && (
                  <p className={styles.notice}>
                    {t('schedule.pay.prepayUnavailable')}
                  </p>
                )}
                {isVendor && (
                  <p className={styles.notice}>
                    Fleet bookings are invoiced to the fleet, so the prepay
                    option does not apply.
                  </p>
                )}
              </div>

              {hasDiscountedPrice && (
              <div className={cx(styles.lineItem, styles.notice)}>
                <span className={styles.strongText}>
                  {t('schedule.planNote.label')}
                </span>
                  {' '}
                {t('schedule.planNote.body')}
              </div>
              )}

              {(isManager || isTester || isSales) && vans.length > 1 && (
              <div className={styles.formHeader}>
                <h4 aria-level={2}>Van Selection</h4>
                <p>Select a van for this booking.</p>
                <PreviousSelector
                  data={
                    vans.map((van) => `${van.name}`)
                  }
                  name="van"
                  selected={
                    vans.findIndex((van) => van.id === selectedVanId)
                  }
                  onChange={(selection) => {
                    if (typeof selection === 'number' && vans[selection]) {
                      setSelectedVanId(vans[selection].id);
                    }
                  }}
                  childClassName={styles.formSubsection}
                  hideNewOption
                  newLabel="Select a new van"
                  loading={false}
                  error={false}
                >
                  <p>
                    Please ensure you select the correct van for this booking.
                  </p>
                </PreviousSelector>
              </div>

              )}

              {!aperio && contactSection}

              <Alert
                message={errorCode === 'TIME_SLOT_UNAVAILABLE'
                  ? ''
                  : errorMessage}
                type="error"
                role="alert"
              />
            </form>
          </div>
        </div>
        <div className={cx(
          styles.bodyRight,
          'container__col-xs-4',
        )}
        >
          {referralCode && !discountCodeId && (
            <p className="apr-referral-note" role="note">
              <strong>{t('schedule.referral.title')}</strong>
              {' '}
              {friendRewardCents > 0
                ? t('schedule.referral.reward', {
                  amount: friendRewardCents % 100
                    ? formatMoney(friendRewardCents / 100)
                    : formatWholeDollars(friendRewardCents / 100),
                })
                : t('schedule.referral.noReward')}
            </p>
          )}
          {themed && <ServiceAddressSummary />}
          <OrderSummary
            onDiscountUpdate={handleDiscountUpdate}
            isVendor={isVendor}
            email={
              ((isManager || isTechnician || isTester || isSales)
              && !customerId)
              || !isLoggedIn
                ? email
                : undefined
}
          />
        </div>
      </div>
      <div className={cx(styles.footer, { 'apr-step__footer': themed })}>
        {aperio ? (
          // The price strip rides in this bar (one pinned bar on a phone);
          // it names the prepay price when prepay is the chosen way to pay.
          <BookingSummaryBar placement="footer" />
        ) : (
          <span className={styles.footerNote}>
            No payment required to book · Free rescheduling
          </span>
        )}
        {(userInfo?.getUserById?.vendorId === 1) ? (
          <Button
            onClick={() => goBackTo(navigate, ROUTE_BOOK_SERVICES)}
            variant="tertiary"
            className={cx(styles.selectButton, { 'apr-step__back': themed })}
          >
            {t('common.back')}
          </Button>
        ) : (
          <Button
            onClick={() => goBackTo(
              navigate,
              // Plan bookings skip the add-ons step in both directions.
              isPlanBooking(selectedServices)
                ? ROUTE_BOOK_SERVICES
                : ROUTE_BOOK_ADD_ONS,
            )}
            variant="tertiary"
            className={cx(styles.selectButton, { 'apr-step__back': themed })}
          >
            {t('common.back')}
          </Button>
        )}

        {/* Always offered: a refused booking can be tried again (the
            idempotency key makes a repeat return the same order). It used to
            vanish after any error the customer could not fix by editing a
            field, which left the step with no way forward. */}
        <Button
          variant="primary"
          // Aperio: drawn as the live primary button even while something is
          // missing (apr-lock, public/aperio/theme.css). It guides rather
          // than refuses: a press marks the missing fields and goes there.
          className={cx(styles.selectButton, { 'apr-lock': aperio })}
          onClick={handleLockIn}
          inactive={submitting}
          softDisabled={!shouldEnableNext}
          aria-describedby={
            !shouldEnableNext ? 'schedule-missing' : undefined
          }
          loading={bookingLoading}
        >
          {(() => {
            if (isStaff) return 'Book appointment';
            return dateTime?.isBooked
              ? t('schedule.submit.request')
              : t('schedule.submit.lock');
          })()}
        </Button>
        {!shouldEnableNext && (
          <span
            id="schedule-missing"
            className={styles.disabledReason}
            ref={missingRef}
            tabIndex={-1}
            style={{ outline: 'none' }}
          >
            {missingText}
          </span>
        )}
        {/* The same list as a polite live region once a booking attempt has
            been refused: read out then, and again as it shortens (QA
            2026-09-25 #30). Always in the page, so the region exists before
            its text changes; off-screen and out of the bar's flex flow, so
            the bar looks as it did. */}
        <span aria-live="polite" style={VISUALLY_HIDDEN}>
          {attempted && !shouldEnableNext ? missingText : ''}
        </span>
      </div>
    </div>
  );
}
